import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment, useMemo } from 'react';
import { NavMarker } from 'react-rhumb';
import { useLocation, useParams } from 'react-router-dom';
import { useQuery } from 'data-fetching-client';
import * as RequestStates from 'marketplace-ui-common/requestStates/RequestState';
import { getDataFetchingClientRequestState } from 'marketplace-ui-common/dataFetchingClient/utils/getDataFetchingClientRequestState';
import DocumentHead from 'ecosystem-marketplace-core/components/portals/DocumentHead';
import getDataFetchingClientStatus from 'marketplace-ui-common/dataFetchingClient/utils/getDataFetchingClientErrorStatus';
import HSPageViewTracker from 'marketplace-ui-common/tracking/components/HSPageViewTracker';
import MetaRobots from 'ecosystem-marketplace-core/components/elements/MetaRobots';
import PrerenderReady from 'ecosystem-marketplace-core/components/prerender/PrerenderReady';
import PrerenderStatusCode from 'ecosystem-marketplace-core/components/prerender/PrerenderStatusCode';
import ThemeDetailsResponse from 'marketplace-ui-assets-core/details/records/ThemeDetailsResponse';
import { useAppDispatch } from '../../reducers';
import { getRootUrl } from '../../utils/urls';
import { GET_THEME_DETAILS } from '../queries/themeDetailsQuery';
import { PublicAppContext } from '../../selectors/app';
import NotFoundError from '../../components/error/NotFoundError';
import NoAccessError from '../../components/error/NoAccessError';
import GenericError from '../../components/error/GenericError';
import StorefrontUsageTrackerProvider from '../../context/StorefrontUsageTrackerProvider';
import ThemeDetailsPage from '../components/ThemeDetailsPage';
import { useEffect } from 'react';
import { resetThemeSlug, updateThemeSlug } from '../../actions/ThemeDetailsActions';

const ThemeDetailsError = ({
  children
}) => /*#__PURE__*/_jsxs(Fragment, {
  children: [/*#__PURE__*/_jsx(PublicAppContext, {
    children: /*#__PURE__*/_jsxs(DocumentHead, {
      children: [/*#__PURE__*/_jsx(MetaRobots, {
        directive: ['nofollow', 'noindex']
      }), /*#__PURE__*/_jsx(PrerenderReady, {}), /*#__PURE__*/_jsx(PrerenderStatusCode, {
        statusCode: 404
      })]
    })
  }), children]
});

export default function ThemeDetailsContainer() {
  const {
    pathname
  } = useLocation();
  const {
    slug
  } = useParams();
  const appDispatch = useAppDispatch();
  const {
    loading,
    error,
    data,
    previousData
  } = useQuery(GET_THEME_DETAILS, {
    variables: {
      slug
    }
  });
  const themeDetails = useMemo(() => {
    const themeDetailsData = data || previousData;
    return new ThemeDetailsResponse(themeDetailsData ? themeDetailsData.details : undefined);
  }, [data, previousData]);
  const requestState = getDataFetchingClientRequestState(error, loading);
  useEffect(() => {
    appDispatch(updateThemeSlug(slug));
    return () => {
      appDispatch(resetThemeSlug());
    };
  }, [appDispatch, slug]);

  if (error) {
    switch (getDataFetchingClientStatus(error)) {
      case 403:
        {
          return /*#__PURE__*/_jsx(ThemeDetailsError, {
            children: /*#__PURE__*/_jsx(NavMarker, {
              name: "THEME_DETAILS_403_LOAD",
              children: /*#__PURE__*/_jsx(NoAccessError, {})
            })
          });
        }

      case 404:
        {
          return /*#__PURE__*/_jsx(ThemeDetailsError, {
            children: /*#__PURE__*/_jsx(NavMarker, {
              name: "THEME_DETAILS_404_LOAD",
              children: /*#__PURE__*/_jsx(NotFoundError, {})
            })
          });
        }

      default:
        {
          return /*#__PURE__*/_jsx(ThemeDetailsError, {
            children: /*#__PURE__*/_jsx(NavMarker, {
              name: "THEME_DETAILS_ERROR_LOAD",
              children: /*#__PURE__*/_jsx(GenericError, {})
            })
          });
        }
    }
  }

  return /*#__PURE__*/_jsxs(StorefrontUsageTrackerProvider, {
    children: [requestState === RequestStates.SUCCEEDED ? /*#__PURE__*/_jsx(ThemeDetailsPage, {
      themeDetails: themeDetails
    }, `theme-details-${slug}`) : null, /*#__PURE__*/_jsx(HSPageViewTracker, {
      pathname: `${getRootUrl()}${pathname}`
    })]
  });
}